import type {
  CreateGenerationQueueItemRequest,
  GenerationQueueItemResponse,
  GenerationQueueResponse,
  GenerationQueueStateResponse,
  ReorderGenerationQueueRequest,
} from '@malaberg/shared';
import { GenerationQueueResponseSchema } from '@malaberg/shared';
import { apiClient } from '../client';

export interface ListGenerationQueueQuery {
  userId?: string;
}

export interface GenerationQueueItemMutationResponse {
  ok: true;
  state: GenerationQueueStateResponse;
  item: GenerationQueueItemResponse;
}

export interface GenerationQueueItemsMutationResponse {
  ok: true;
  state: GenerationQueueStateResponse;
  items: GenerationQueueItemResponse[];
}

export interface GenerationQueueResumeResponse {
  ok: true;
  state: GenerationQueueStateResponse;
}

export interface GenerationQueueCancelRemainingResponse {
  ok: true;
  cancelledCount: number;
}

function toQueryString(params: ListGenerationQueueQuery): string {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined);
  if (entries.length === 0) return '';
  return `?${entries
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&')}`;
}

export const generationQueueApi = {
  list(query: ListGenerationQueueQuery = {}): Promise<GenerationQueueResponse> {
    return apiClient.request<GenerationQueueResponse>(
      'GET',
      `/api/generation-queue${toQueryString(query)}`,
      { schema: GenerationQueueResponseSchema }
    );
  },

  add(body: CreateGenerationQueueItemRequest): Promise<GenerationQueueItemMutationResponse> {
    return apiClient.request<GenerationQueueItemMutationResponse>(
      'POST',
      '/api/generation-queue/items',
      {
        body: JSON.stringify(body),
      }
    );
  },

  edit(
    id: string,
    body: CreateGenerationQueueItemRequest
  ): Promise<GenerationQueueItemMutationResponse> {
    return apiClient.request<GenerationQueueItemMutationResponse>(
      'PATCH',
      `/api/generation-queue/items/${id}`,
      { body: JSON.stringify(body) }
    );
  },

  reorder(body: ReorderGenerationQueueRequest): Promise<GenerationQueueItemsMutationResponse> {
    return apiClient.request<GenerationQueueItemsMutationResponse>(
      'PATCH',
      '/api/generation-queue/reorder',
      { body: JSON.stringify(body) }
    );
  },

  cancel(id: string): Promise<GenerationQueueItemMutationResponse> {
    return apiClient.request<GenerationQueueItemMutationResponse>(
      'POST',
      `/api/generation-queue/items/${id}/cancel`
    );
  },

  cancelRemaining(): Promise<GenerationQueueCancelRemainingResponse> {
    return apiClient.request<GenerationQueueCancelRemainingResponse>(
      'POST',
      '/api/generation-queue/cancel-remaining'
    );
  },

  requeue(
    id: string,
    body: CreateGenerationQueueItemRequest
  ): Promise<GenerationQueueItemMutationResponse> {
    return apiClient.request<GenerationQueueItemMutationResponse>(
      'POST',
      `/api/generation-queue/items/${id}/requeue`,
      { body: JSON.stringify(body) }
    );
  },

  resume(): Promise<GenerationQueueResumeResponse> {
    return apiClient.request<GenerationQueueResumeResponse>('POST', '/api/generation-queue/resume');
  },
};
